import { CssBaseline, MuiThemeProvider,Typography, Theme } from '@material-ui/core';
import { createStyles, makeStyles, ThemeProvider } from '@material-ui/styles';
import AppHeader from 'da-frontend-shared-ui-components/lib/AppHeader';
import AppHeaderRootLink from 'da-frontend-shared-ui-components/lib/AppHeader/AppHeaderRootLink';
import Footer from 'da-frontend-shared-ui-components/lib/Footer';
import { Data as Dashboard, HelpBound, Logout, PhoneBound, Tools, User } from 'da-frontend-shared-ui-components/lib/icons';
import * as colors from 'da-frontend-shared-ui-components/lib/styles/colors';
import defaultTheme from 'da-frontend-shared-ui-components/lib/styles/themes/defaultTheme';
import React, { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom"

type LayoutContextType = {
  firstName?: string
  loggedIn?: boolean
}

const LayoutContext = createContext<[LayoutContextType, (x: LayoutContextType) => void]>([{}, null]);

const useStyles = makeStyles((theme: Theme) => 
  createStyles({
    "@global": {
      body: {
        fontFamily: theme.typography.fontFamily,
        overflowY: "scroll",
      },
      a: {
        textDecoration: "none",
        fontWeight: 600,
        color: colors.teal,
        cursor: "pointer"
      },
      pre: {
        fontFamily: theme.typography.fontFamily,
        padding: 0,
        margin: 0
      }
    },
    main: {
      padding: /*theme.spacing.unit * */ '2em',
      paddingBottom: "4em"
    },
    primaryIcon: {
      color: colors.white,
      fontSize: "1.25rem",
      paddingRight: "5px",
    },
    secondaryIcon: {
      fontSize: "0.75rem",
      paddingRight: "5px",
    },
  })
);

const MainSection = ({children}) => {
  const classes = useStyles();

  return <div className={classes.main}>{children}</div>;
}

const Header = () => {
  const styles                    = useStyles();
  const { t }                     = useTranslation();
  const [{ firstName, loggedIn }] = useContext(LayoutContext);

  return (
  <AppHeader
    title={
      <AppHeaderRootLink href="/">
        PowerCommand
        <div>Cloud&trade;</div>
      </AppHeaderRootLink>
    }
    greeting={ firstName && t("nav.Hello", { firstName }) }
    primaryNavigationLinks={[
      { label: t`nav.Dashboard`, href: "/dashboard", 
        icon: props => <Dashboard className={styles.primaryIcon} {...props} /> 
      },
      { label: t`nav.Account Management`, path: "", 
        icon: props => <Tools className={styles.primaryIcon} {...props} />, isActive: true 
      },
    ]} 
    secondaryNavigationLinks={ loggedIn ? [
      { label: t`nav.Profile`, href: "/profile", 
        icon: props => <User className={styles.secondaryIcon} {...props} />
      },
      { label: t`nav.Customer Support`, href: "/customer-support",
        icon: props => <PhoneBound className={styles.secondaryIcon} {...props} />
      },
      { label: t`nav.Help`, href: "/help",
        icon: props => <HelpBound className={styles.secondaryIcon} {...props} />
      },
      { label: t`nav.Log Out`, href: "/logout", 
        icon: props => <Logout className={styles.secondaryIcon} {...props} />
      },
    ] : [] } />
  )
}

/**
 * Layout shared between all Account Management pages
 */
const Layout = ({ children, aboveHeader }: { children: React.ReactNode, aboveHeader?: React.ReactNode }) => {
  // const classes = useStyles()
  const [t] = useTranslation()
  return (
  <LayoutContext.Provider value={useState({})}>
    <CssBaseline />
    <MuiThemeProvider theme={defaultTheme}>
    <ThemeProvider theme={defaultTheme}>
      {aboveHeader}
      <Header />
      <MainSection>{children}</MainSection>
      <Footer
        links={{
          driver: (props) => (
            <Link to={"/driver_download"}>
              <Typography {...props}>{t("nav.footer.downloadDrivers")}</Typography>
            </Link>
          ),
          privacy: (props) => (
            <Link to={"/privacy_policy"}>
              <Typography {...props}>{t("nav.footer.privacyPolicy")}</Typography>
            </Link>
          ),
          terms: (props) => (
            <Link to={"/terms_and_conditions"}>
              <Typography {...props}>{t("nav.footer.termsAndConditions")}</Typography>
            </Link>
          ),
        }}
      />
    </ThemeProvider>
    </MuiThemeProvider>
  </LayoutContext.Provider>
);
}

export function useLayoutContext() {
  const [ , setContext ] = useContext(LayoutContext);

  return setContext;
}

export default Layout;